import { GridRowSelectionModel } from '@mui/x-data-grid-premium'
import type { KnownBlock } from '@slack/types'
import { useUpdateLead } from 'api/aws/leads/useUpdateLead'
import { actions, button, divider, section } from 'api/aws/notifications/slackBlocks'
import { useUniversalNotify } from 'api/aws/notifications/useUniversalNotify'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { useCallback } from 'react'
import { Lead, User } from 'types'

interface UseProcessLeadRowUpdateProps {
  users: User[]
}

export const useProcessLeadRowUpdate = ({ users }: UseProcessLeadRowUpdateProps) => {
  const { mutateAsync: updateLead } = useUpdateLead()
  const { notify } = useUniversalNotify()
  const { showSnackbar } = useSnackbarContext()

  const processRowUpdate = useCallback(
    async (newRow: Lead, oldRow: Lead, _rowSelectionModel?: GridRowSelectionModel): Promise<Lead> => {
      // Determine which fields changed
      const changedFields: Partial<Lead> = {}
      const editableFields = ['status', 'quality', 'assignedUser', 'leadScore', 'leadScoreDA', 'leadScoreIndustry'] as const

      for (const field of editableFields) {
        if (newRow[field] !== oldRow[field]) {
          ;(changedFields as Record<string, unknown>)[field] = newRow[field]
        }
      }

      // If nothing changed, return the row as-is
      if (Object.keys(changedFields).length === 0) {
        return newRow
      }

      try {
        // Update the lead in the database
        await updateLead({
          lead_uuid: newRow.lead_uuid,
          updates: changedFields,
        })

        // Check if assignedUser changed and send notification
        if (changedFields.assignedUser && changedFields.assignedUser !== oldRow.assignedUser) {
          const newAssignedUser = users.find(u => u.user_uuid === changedFields.assignedUser)
          const senderName = localStorage.getItem('slack_user_name') || 'Someone'
          const senderId = localStorage.getItem('slack_user_id') || ''

          if (newAssignedUser) {
            const leadName = newRow.name || 'Unnamed Lead'
            const leadCompany = newRow.company || ''
            const slackBaseUrl = import.meta.env.VITE_BASE_URL || ''

            // Determine the leads URL based on environment
            const isProd = import.meta.env.VITE_ENV === 'production'
            // const leadsUrl = isProd ? 'https://grobotic.app/leads' : 'https://dev.grobotic.app/leads'
            const leadsUrl = 'https://grobotic.app/leads'

            // Build custom Slack blocks for lead assignment notification
            const customBlocks: KnownBlock[] = [
              section(`Hi <@${newAssignedUser.user_uuid}>, you've been assigned a new lead!`),
              divider(),
              section(`*Lead:* ${leadName}${leadCompany ? ` (${leadCompany})` : ''}`),
              section(`*Assigned by:* <@${senderId}>`),
              ...(newRow.email ? [section(`*Email:* ${newRow.email}`)] : []),
              ...(newRow.phone ? [section(`*Phone:* ${newRow.phone}`)] : []),
              ...(newRow.message
                ? [section(`*Message:* ${newRow.message.substring(0, 200)}${newRow.message.length > 200 ? '...' : ''}`)]
                : []),
              actions([button('Open Leads', leadsUrl)]),
            ]

            // Send notification to newly assigned user
            await notify({
              data: {
                users: [newAssignedUser],
                type: 'assignment',
                severity: 'info',
                title: 'New Lead Assigned',
                message: `You've been assigned a new lead: ${leadName}`,
                board: 'leads',
                boardItemName: leadName,
                boardId: newRow.lead_uuid,
                slackHeaderMessage: `You've been assigned a new lead!`,
                slackMessageHtml: `Lead: ${leadName}${leadCompany ? ` (${leadCompany})` : ''}`,
                customSlackBlocks: customBlocks,
                slackDirectLink: '/leads',
                sendSlack: true,
                sendApp: true,
              },
            })
          }
        }

        showSnackbar('Lead updated successfully', 'success')
        return newRow
      } catch (error) {
        console.error('Failed to update lead:', error)
        showSnackbar('Failed to update lead', 'error')
        // Return old row to revert the change in the grid
        return oldRow
      }
    },
    [updateLead, users, notify, showSnackbar]
  )

  const handleProcessRowUpdateError = useCallback(
    (error: Error) => {
      console.error('Row update error:', error)
      showSnackbar('Failed to update lead', 'error')
    },
    [showSnackbar]
  )

  return { processRowUpdate, handleProcessRowUpdateError }
}
